import React, { useEffect, useRef } from "react";
import { Html5Qrcode, Html5QrcodeScannerState, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { X } from "lucide-react";

// Accept both QR codes AND common barcode formats used by Apple/Google Wallet
// loyalty passes (PDF417 for Apple, QR/AZTEC/CODE128 for Google), plus retail
// barcodes (EAN/UPC). This lets the operator scan the card straight from the
// customer's Wallet pass without needing them to open the app QR.
const SUPPORTED_FORMATS = [
  Html5QrcodeSupportedFormats.QR_CODE,
  Html5QrcodeSupportedFormats.PDF_417,
  Html5QrcodeSupportedFormats.AZTEC,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.DATA_MATRIX,
];

export default function QrScanner({ open, onClose, onDecoded, title = "Inquadra il QR, il barcode Wallet o il codice EAN-13 / CODE-128 della carta" }) {
  const boxId = "qr-scan-box";
  const scannerRef = useRef(null);
  const onDecodedRef = useRef(onDecoded);
  useEffect(() => { onDecodedRef.current = onDecoded; }, [onDecoded]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const html5 = new Html5Qrcode(boxId, { formatsToSupport: SUPPORTED_FORMATS, verbose: false });
    scannerRef.current = html5;
    (async () => {
      try {
        await html5.start(
          { facingMode: "environment" },
          {
            fps: 10,
            // Slightly larger scan box helps with wide PDF417 codes typical of Apple Wallet
            qrbox: { width: 280, height: 200 },
            aspectRatio: 1.4,
          },
          (decoded) => {
            if (cancelled) return;
            cancelled = true;
            onDecodedRef.current?.(decoded);
          },
          () => {}
        );
      } catch (e) {
        console.error("QR start error", e);
      }
    })();
    return () => {
      cancelled = true;
      const scanner = scannerRef.current;
      scannerRef.current = null;
      if (!scanner) return;
      // Must await stop() before clear() — otherwise the library throws
      // "Cannot clear while scan is ongoing".
      (async () => {
        try {
          const state = scanner.getState?.();
          if (state === Html5QrcodeScannerState.SCANNING ||
              state === Html5QrcodeScannerState.PAUSED) {
            await scanner.stop();
          }
        } catch (_) { /* ignore */ }
        try {
          scanner.clear();
        } catch (_) { /* ignore */ }
      })();
    };
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="qr-scanner-modal">
      <div className="w-full max-w-md card-elev p-4 relative">
        <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-md hover:bg-zinc-800" data-testid="qr-scanner-close">
          <X className="h-4 w-4" />
        </button>
        <h3 className="font-heading font-semibold mb-3">{title}</h3>
        <div id={boxId} className="rounded-xl overflow-hidden bg-black" />
        <p className="text-xs text-zinc-500 mt-3">
          Consenti l'accesso alla fotocamera. Vengono riconosciuti QR code, PDF417 (Apple Wallet), Aztec, Code128 e i barcode comuni.
        </p>
      </div>
    </div>
  );
}
